import { create } from 'zustand';
import { apiRequest } from '../lib/api';
import { useScrollStore } from './scrollStore';
import { ProductItem } from '../types/api';
import { PAGE_SIZE } from '../lib/constants';

interface CatalogState {
  items: ProductItem[];
  viewMode: 'home' | 'search';
  query: string | null;
  loading: boolean;
  error: string | null;
  visibleCount: number;
  fetchSearch: (query: string) => Promise<void>;
  /** Reveal the next page of already-loaded search results. */
  showMore: () => void;
  clearCatalog: () => void;
}

const normalizeItem = (item: ProductItem): ProductItem => ({
  item_id: item.item_id,
  in_stock: item.in_stock ?? true,
  thumbnail_url: item.thumbnail_url ?? null,
  thumbnail_url_2x: item.thumbnail_url_2x ?? null,
  image_url: item.image_url,
  description: item.description,
  price: Number(item.price),
  pricing: item.pricing,
  display_name: item.display_name,
  fabric: item.fabric ?? [],
  color: item.color ?? [],
  product_type: item.product_type,
  manufacturer: item.manufacturer,
  country_of_origin: item.country_of_origin ?? null,
  image_urls: item.image_urls,
});

const parseWords = (query: string): string[] =>
  query
    .split(/[\s,]+/)
    .map((word) => word.trim())
    .filter(Boolean);

let searchRequest = 0;

export const useCatalogStore = create<CatalogState>((set) => ({
  items: [],
  viewMode: 'home',
  query: null,
  loading: false,
  error: null,
  visibleCount: PAGE_SIZE,

  fetchSearch: async (query: string) => {
    const request = ++searchRequest;
    const words = parseWords(query);
    const params = new URLSearchParams();
    words.forEach((word) => params.append('words', word));

    set({
      viewMode: 'search',
      query,
      loading: true,
      error: null,
      visibleCount: PAGE_SIZE,
    });
    useScrollStore.getState().forget('search');

    try {
      const data = await apiRequest<ProductItem[]>(`/search?${params}`, {
        fallbackError: (status) => `Request failed: ${status}`,
      });
      if (request !== searchRequest) return;
      set({
        items: data.map(normalizeItem),
        viewMode: 'search',
        query,
        loading: false,
        error: null,
      });
    } catch (err) {
      if (request !== searchRequest) return;
      set({
        items: [],
        viewMode: 'search',
        query,
        loading: false,
        error: err instanceof Error ? err.message : 'Search failed',
      });
    }
  },

  showMore: () =>
    set((state) => ({ visibleCount: Math.min(state.visibleCount + PAGE_SIZE, state.items.length) })),

  clearCatalog: () => {
    searchRequest++;
    set({
      items: [],
      viewMode: 'home',
      query: null,
      loading: false,
      error: null,
      visibleCount: PAGE_SIZE,
    });
    // Returning home via the logo starts both listings from the top.
    useScrollStore.getState().forget('home', 'search');
  },
}));
